import { type InputProps, TextInput } from "@mantine/core";
import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { Select } from "src/components/shared/MantineCombobox";
import { useTankPlans } from "src/hooks/useTankPlans";

interface Props extends InputProps {
  id: string;
  type?: string;
  tanktype?: string;
  value:
    | {
        key?: string;
        value?: string | { key: string };
        data?: { tankid: string; tankName: string };
      }
    | string;
}
const TankSelect = ({ type, id, value, tanktype }: Props) => {
  const { control, setValue } = useFormContext();

  const {
    field: { value: tankValue },
  } = useController({
    name: id,
    defaultValue: value,
    control,
  });

  const { tanks, filter } = useTankPlans();

  const filteredTanks = useMemo(() => {
    if (type === "orb" && tanktype) {
      return filter({ orbtype: tanktype });
    }
    if (type === "ballast") {
      return filter({ bwl: true });
    }
    if (type === "msfbA") {
      return filter({ mfsbA: true });
    }
    if (type === "msfbB") {
      return filter({ mfsbB: true });
    }
    return tanks;
  }, [filter, tanks, type, tanktype]);

  const parsedTanks = useMemo(() => {
    const parsed = filteredTanks.map(tank => ({
      label: tank.name,
      value: tank.tankid,
    }));
    if (
      value &&
      typeof value === "object" &&
      value.key &&
      !parsed.some(tank => tank.value === value.key)
    ) {
      parsed.push({
        label:
          typeof value.value === "string"
            ? value.value
            : (value.value?.key ?? value.key),
        value: value.key,
      });
    }
    return parsed;
  }, [filteredTanks, value]);

  const handleSelectChange = (selected: string) => {
    setValue(id, {
      key: selected,
      value:
        parsedTanks.find(tank => tank.value === selected)?.label ?? selected,
    });
  };

  return parsedTanks && parsedTanks.length > 0 ? (
    <Select
      value={tankValue?.key ?? ""}
      onChange={handleSelectChange}
      data={parsedTanks}
      required={false}
      placeholder="Select Tank"
      dropdownPosition="top"
      withinPortal={true}
      zIndex={1000}
    />
  ) : (
    <TextInput
      value={tankValue?.value ?? ""}
      placeholder="No Tanks Available"
      onChange={e => handleSelectChange(e.currentTarget.value)}
    />
  );
};
export default TankSelect;
